import { isCollapsibleGroup } from '../applyCollapsedGroupIds';
import { getDistinctKey, getUncollapsedAncestor } from '../collapseLinks';
import type { RelationshipsLink, RelationshipsNode } from '../types';

export type OutlineLink = {
  key: string;
  label: string;
};

export type OutlineItem = {
  id: string;
  label: string;
  items: OutlineItem[];
  links: OutlineLink[];
};

type ToOutlineArgs = {
  rootNode: RelationshipsNode;
  links: RelationshipsLink[];
};

const showsChildren = (node: RelationshipsNode): boolean =>
  !isCollapsibleGroup(node) || node.open;

const toVisibleLinksBySource = (
  links: RelationshipsLink[]
): Map<RelationshipsNode, OutlineLink[]> => {
  const bySource = new Map<RelationshipsNode, Map<string, OutlineLink>>();
  for (const link of links) {
    const source = getUncollapsedAncestor(link.source);
    const target = getUncollapsedAncestor(link.target);
    if (source === target) continue;
    const key = getDistinctKey(link, source, target);
    const label = [link.label, target.label].filter(Boolean).join(' ');
    const sourceLinks = bySource.get(source) ?? new Map();
    sourceLinks.set(key, { key, label });
    bySource.set(source, sourceLinks);
  }
  return new Map(
    [...bySource].map(([source, sourceLinks]) => [
      source,
      [...sourceLinks.values()],
    ])
  );
};

const toItems = (
  nodes: RelationshipsNode[],
  linksBySource: Map<RelationshipsNode, OutlineLink[]>
): OutlineItem[] =>
  nodes.flatMap(node => {
    const children = showsChildren(node) ? (node.children ?? []) : [];
    const items = toItems(children, linksBySource);
    if (node.isSynthetic) return items;
    return [
      {
        id: node.id,
        label: node.label,
        items,
        links: linksBySource.get(node) ?? [],
      },
    ];
  });

/**
 * The groups and components the Relationships view draws, nested as drawn,
 * each with the links drawn from it. Collapsed groups hide their contents and
 * take over their links, and layout-only containers are left out.
 */
export const toOutline = ({ rootNode, links }: ToOutlineArgs): OutlineItem[] =>
  toItems([rootNode], toVisibleLinksBySource(links));
